/**
 * Product Card Component
 *
 * Displays a single product in the grid.
 */

import { useState } from 'react';
import { useCart, useShop, useWishlist, useAuth } from '@/context';
import type { Product } from '@/types';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const { addItem, isLoading: cartLoading } = useCart();
  const { isInWishlist, addItem: addToWishlist, removeItem: removeFromWishlist } = useWishlist();
  const { isLoggedIn } = useAuth();
  const { t } = useShop();
  const formatPrice = (amount: number) =>
    Math.floor(amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const [isAdding, setIsAdding] = useState(false);

  const inWishlist = isInWishlist(product.id);
  const hasVariations = Array.isArray(product.variations) && product.variations.length > 0;
  const isOutOfStock = product.stock_status === 'outofstock';

  const handleAddToCart = async () => {
    if (hasVariations) {
      // Redirect to product page for variant selection
      window.location.href = product.permalink;
      return;
    }

    setIsAdding(true);
    try {
      await addItem({ product_id: product.id, quantity: 1 });
    } catch (err) {
      console.error('Failed to add to cart:', err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleWishlistClick = () => {
    if (!isLoggedIn) {
      window.location.href = window.evcShop.urls.login;
      return;
    }

    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  return (
    <article className={styles.card}>
      {/* Badges */}
      <div className={styles.badges}>
        {product.on_sale && <span className={styles.saleBadge}>Promo</span>}
        {isOutOfStock && <span className={styles.outOfStockBadge}>Épuisé</span>}
      </div>

      {/* Action buttons - top right */}
      <div className={styles.imageActions}>
        <button
          className={styles.addToCartButton}
          onClick={handleAddToCart}
          disabled={isOutOfStock || isAdding || cartLoading}
          aria-label={t('addToCart')}
        >
          <CartIcon />
        </button>
        <button
          className={`${styles.wishlistButton} ${inWishlist ? styles.inWishlist : ''}`}
          onClick={handleWishlistClick}
          aria-label={inWishlist ? t('removeFromWishlist') : t('addToWishlist')}
        >
          <HeartIcon filled={inWishlist} />
        </button>
      </div>

      {/* Image */}
      <a href={product.permalink} className={styles.imageLink}>
        <div className={styles.image}>
          {product.featured_image ? (
            <img
              src={product.featured_image.url}
              alt={product.featured_image.alt || product.name}
              loading="lazy"
            />
          ) : (
            <div className={styles.placeholder}>
              <ImageIcon />
            </div>
          )}
        </div>
      </a>

      {/* Content */}
      <div className={styles.content}>
        <a href={product.permalink} className={styles.nameLink}>
          <h3 className={styles.name}>{product.name}</h3>
        </a>

        <div className={styles.price}>
          {product.on_sale && product.sale_price ? (
            <>
              <span className={styles.salePrice}>{formatPrice(product.sale_price)}</span>
              {product.regular_price && (
                <span className={styles.regularPrice}>{formatPrice(product.regular_price)}</span>
              )}
            </>
          ) : (
            <span>{formatPrice(product.price)}</span>
          )}
        </div>
      </div>
    </article>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}
